import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Flex, Text, Button, Spinner, Center, VStack, Stack, Select, Input, Table, Thead, Tbody,
  Tr, Th, Td, FormControl, FormLabel,
} from "@chakra-ui/react";
import { Download, FileText } from "lucide-react";
import { getDifalDevolucoes, baixarDifalDevolucoesCsv } from "../../services/api";
import { formatBRL, formatDate } from "../../utils/formatters";

const ACCENT = "#F68633";

export default function RelatorioDifalDevolucoes({ toast }) {
  const [linhas, setLinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [baixando, setBaixando] = useState(false);
  const [filtros, setFiltros] = useState({ ambiente: "producao", data_inicio: "", data_fim: "" });

  const params = useCallback(() => ({
    ambiente: filtros.ambiente || undefined,
    data_inicio: filtros.data_inicio || undefined,
    data_fim: filtros.data_fim || undefined,
  }), [filtros]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const res = await getDifalDevolucoes(params());
    if (res?.error) {
      toast?.({ title: "Não foi possível carregar", description: res.error, status: "error", duration: 6000 });
    }
    setLinhas(res?.data || []);
    setCarregando(false);
  }, [params, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  const baixar = async () => {
    setBaixando(true);
    const res = await baixarDifalDevolucoesCsv(params());
    setBaixando(false);
    if (res?.error) {
      toast?.({ title: "Não foi possível baixar", description: res.error, status: "error", duration: 6000 });
    }
  };

  const total = linhas.reduce((s, l) => s + Number(l.valor_icms_uf_dest || 0) + Number(l.valor_fcp_uf_dest || 0), 0);

  return (
    <Box>
      <Flex gap={3} mb={4} wrap="wrap" align="flex-end">
        <FormControl w={{ base: "100%", sm: "170px" }}>
          <FormLabel fontSize="xs" color="gray.600" mb={1}>Ambiente</FormLabel>
          <Select size="sm" bg="white" value={filtros.ambiente}
            onChange={(e) => setFiltros((f) => ({ ...f, ambiente: e.target.value }))}>
            <option value="producao">Produção</option>
            <option value="homologacao">Homologação</option>
            <option value="">Todos</option>
          </Select>
        </FormControl>
        <FormControl w={{ base: "48%", sm: "150px" }}>
          <FormLabel fontSize="xs" color="gray.600" mb={1}>De</FormLabel>
          <Input size="sm" type="date" bg="white" value={filtros.data_inicio}
            onChange={(e) => setFiltros((f) => ({ ...f, data_inicio: e.target.value }))} />
        </FormControl>
        <FormControl w={{ base: "48%", sm: "150px" }}>
          <FormLabel fontSize="xs" color="gray.600" mb={1}>Até</FormLabel>
          <Input size="sm" type="date" bg="white" value={filtros.data_fim}
            onChange={(e) => setFiltros((f) => ({ ...f, data_fim: e.target.value }))} />
        </FormControl>
        <Button size="sm" variant="outline" leftIcon={<Download size={14} />} isLoading={baixando}
          isDisabled={linhas.length === 0} onClick={baixar} w={{ base: "100%", sm: "auto" }}>
          Baixar CSV
        </Button>
      </Flex>

      {carregando ? (
        <Center py={16}><Spinner color={ACCENT} /></Center>
      ) : linhas.length === 0 ? (
        <Center py={16}>
          <VStack spacing={2}>
            <FileText size={36} color="#CBD5E0" />
            <Text color="gray.500" fontSize="sm">Nenhuma devolução com DIFAL no período.</Text>
          </VStack>
        </Center>
      ) : (
        <>
          <Text fontSize="sm" mb={3}>
            {linhas.length} devolução(ões) · DIFAL + FCP a restituir <b>{formatBRL(total)}</b>
          </Text>

          <Stack display={{ base: "flex", md: "none" }} spacing={2}>
            {linhas.map((l) => (
              <Box key={l.id} bg="white" borderWidth="1px" borderRadius="lg" p={3}>
                <Flex justify="space-between" gap={2}>
                  <Text fontSize="sm" fontWeight="semibold">NF-e {l.nfe_numero}/{l.nfe_serie} · {l.uf || "-"}</Text>
                  <Text fontSize="sm" fontWeight="bold">{formatBRL(Number(l.valor_icms_uf_dest) + Number(l.valor_fcp_uf_dest))}</Text>
                </Flex>
                <Text fontSize="xs" color="gray.500" noOfLines={2}>{l.cliente_nome}</Text>
                <Text fontSize="10px" color="gray.400" wordBreak="break-all" mt={1}>Venda {l.chave_venda}</Text>
              </Box>
            ))}
          </Stack>

          <Box display={{ base: "none", md: "block" }} overflowX="auto" borderWidth="1px" borderRadius="lg" bg="white">
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>NF-e devolução</Th>
                  <Th>Emitida</Th>
                  <Th>UF</Th>
                  <Th>Cliente</Th>
                  <Th isNumeric>ICMS UF destino</Th>
                  <Th isNumeric>FCP UF destino</Th>
                  <Th>NF-e de venda</Th>
                </Tr>
              </Thead>
              <Tbody>
                {linhas.map((l) => (
                  <Tr key={l.id}>
                    <Td fontSize="xs">{l.nfe_numero}/{l.nfe_serie}</Td>
                    <Td fontSize="xs">{formatDate(l.emitida_em, "-")}</Td>
                    <Td fontSize="xs">{l.uf || "-"}</Td>
                    <Td fontSize="xs"><Text noOfLines={1} maxW="220px">{l.cliente_nome}</Text></Td>
                    <Td fontSize="xs" isNumeric>{formatBRL(l.valor_icms_uf_dest)}</Td>
                    <Td fontSize="xs" isNumeric>{formatBRL(l.valor_fcp_uf_dest)}</Td>
                    <Td fontSize="10px" color="gray.500" maxW="190px">
                      <Text wordBreak="break-all">{l.chave_venda}</Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}
